import ReviewsIcon from "@mui/icons-material/Reviews";
import { Dialog, Rating, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import DialogBody from "../dialog/DialogBody";
import DialogHeader from "../dialog/DialogHeader";
import Label from "../label";
import InputField from "../InputField";
import DialogActionButtons from "../dialog/DialogActions";
import { useSelector } from "react-redux";
import { addProductReview } from "../../services/productsFn";
import { useSnackbar } from "../../lib/context/snackbarContext";
import { addReviewValidation } from "../../utils/validation";
import { addReviewInWorker } from "../../services/workerFn";
import { t } from "i18next";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing("24px"),
  },
  "& .MuiPaper-root": {
    width: "500px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
  "& .MuiTableContainer-root .MuiTable-root .MuiTableHead-root": {
    backgroundColor: "#f3f3f3",
  },
  "& .MuiTableCell-root ": {
    padding: "8px 16px",
  },
}));

const ReviewDialog = forwardRef(({}, ref) => {
  const { showSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState();
  const [dialogData, setDialogData] = useState(null);
  const [inputValues, setInputValues] = useState({
    rating: 0,
    comment: "",
  });

  const [inputError, setInputError] = useState({
    rating: false,
    comment: false,
    isValidComment: false,
  });

  const userData = useSelector((state) => state.userReducer.user);

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setDialogData(params);
      setOpen(true);
    },
    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValues({});
    setInputError({});
  };

  const handleChangeInputs = (event) => {
    const { name, value } = event.target;

    if (name === "comment" && value.length > 200) {
      setInputError((err) => ({ ...err, isValidComment: true }));
      return;
    }

    setInputError((err) => ({
      ...err,
      [name]: false,
      isValidComment: name === "comment" ? false : inputError.isValidComment,
    }));
    setInputValues((val) => ({ ...val, [name]: value }));
  };

  const handleAddReviewInProduct = async () => {
    const validate = addReviewValidation(inputValues, setInputError);
    if (validate) return;
    try {
      const payload = {
        productId: dialogData?.data?._id,
        userId: userData._id,
        rating: inputValues.rating,
        comment: inputValues.comment,
        productIndex: dialogData?.index,
        orderId: dialogData?.orderId,
      };
      //   return;
      setIsLoading(true);
      const response = await addProductReview(payload);
      if (response.data.status === "success") {
        handleClose();
        dialogData.getData();
        showSnackbar(response.data.message, "success");
      } else {
        showSnackbar(response.data.message, "error");
      }
    } catch (error) {
      showSnackbar(error?.message, "error");
    } finally {
      setIsLoading(false);
    }
  };
  const handleAddReviewInService = async () => {
    const validate = addReviewValidation(inputValues, setInputError);
    if (validate) return;
    try {
      const payload = {
        userId: userData._id,
        rating: inputValues.rating,
        comment: inputValues.comment,
        workerId: dialogData?.workerId,
        appointmentId: dialogData?.appointmentId,
      };
      //   return;
      setIsLoading(true);
      const response = await addReviewInWorker(payload);
      if (response.data.status === "success") {
        handleClose();
        dialogData.getData();
        showSnackbar(response.data.message, "success");
      } else {
        showSnackbar(response.data.message, "error");
      }
    } catch (error) {
      showSnackbar(error?.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
    >
      <DialogHeader
        title="addReview"
        secondaryHeading={`${t("addReviewForThis")} ${
          dialogData?.type === "product" ? t("product") : t("service")
        }`}
        icon={<ReviewsIcon sx={{ fontSize: "2rem", color: "primary.main" }} />}
        onClose={handleClose}
      />

      <DialogBody>
        <Stack gap={1} direction="row" alignItems="center">
          <Rating
            name="simple-controlled"
            value={inputValues.rating}
            precision={0.5}
            onChange={(event, newValue) => {
              setInputValues((val) => ({ ...val, rating: newValue }));
              setInputError((err) => ({ ...err, rating: false }));
            }}
          />
          <Typography color={inputError.rating ? "red" : "inherit"}>
            {t("chooseRating")}
          </Typography>
        </Stack>
        <Stack mt={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Label text={t("addReview")} required htmlFor="add-comment" />
            <Typography>{inputValues?.comment?.length || 0}/200</Typography>
          </Stack>
          <InputField
            id="add-comment"
            multiline
            rows={3}
            required
            placeholder={t("enterYourReview")}
            inputError={inputError.comment || inputError.isValidComment}
            helperText={
              inputError.comment
                ? t("reviewIsRequired")
                : inputError.isValidComment
                ? t("reviewLengthValidationMsg")
                : null
            }
            name="comment"
            value={inputValues.comment}
            onChange={handleChangeInputs}
          />
        </Stack>
      </DialogBody>
      <DialogActionButtons
        onCancel={handleClose}
        confirmText="add"
        onConfirm={() => {
          if (dialogData?.type === "product") {
            handleAddReviewInProduct();
          } else {
            handleAddReviewInService();
          }
        }}
      />
    </BootstrapDialog>
  );
});

export default ReviewDialog;
